import { Button, Link, ValidationMessage } from '@huntington/honeycomb';
import { Collapse, Stack, TextField, Typography } from '@mui/material';
import {
	useCallback,
	useEffect,
	useState,
	type ChangeEventHandler,
	type FocusEventHandler,
	type SyntheticEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
	useGenerateOTPMutation,
	useResetPasswordMutation,
	useValidateOTPMutation,
} from '../../api/user.api';
import { PasswordInput } from '../../components/Form/PasswordInput';
import {
	PasswordRequirements,
	SuccessMessage,
	validatePassword,
} from '../../components/Form/PasswordRequirements';
import SpinnerModal from '../../components/Modals/SpinnerModal/SpinnerModal';
import {
	CANCEL,
	CONFIRM_CODE_DESCRIPTION,
	CONFIRM_CODE_TITLE,
	CONFIRM_PASSWORD,
	CONTINUE,
	EMAIL_ADDRESS,
	FORGOT_PASSWORD_DESCRIPTION,
	FORGOT_PASSWORD_TITLE,
	INVALID_EMAIL_ERROR,
	NEW_OTP_CODE,
	NEW_PASSWORD,
	NO_EMAIL_ERROR,
	NO_PASSWORD_ERROR,
	NO_VERIFICATION_CODE_ERROR,
	PASSWORD_REQUIREMENT_MATCHING,
	PASSWORD_REQUIREMENT_MEETS,
	PASSWORD_REQUIREMENT_NOT_MATCHING,
	PASSWORD_REQUIREMENT_NOT_MET,
	RESET_PASSWORD_DESCRIPTION,
	RESET_PASSWORD_TITLE,
	SUBMIT,
	VERIFICATION_CODE_LABEL,
	VERIFY_OTP_INPUT_PLACEHOLDER,
} from '../../constants/messages';
import { emailRegex } from '../../hooks/useFormHook';
import { ROUTE_ERROR, ROUTE_FORGOT_PASSWORD, ROUTE_LOGIN } from '../../router/router';
import { ColorBaseCharcoal } from '@huntington/honeycomb-design';

type Step = '1-email' | '2-otp' | '3-reset-password';

// Held at the backend rate-limit cap (50/day) until Ted's UX rework re-tightens it.
export const MAX_OTP_ATTEMPTS = 50;

export default function ForgotPassword() {
	const navigate = useNavigate();
	const [email, setEmail] = useState('');
	const [code, setCode] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [touchedPassword, setTouchedPassword] = useState(false);
	const [inputOneError, setInputOneError] = useState('');
	const [inputTwoError, setInputTwoError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [step, setStep] = useState<Step>('1-email');
	const [otpAttempts, setOtpAttempts] = useState(0);

	const [
		getOTP,
		{ isSuccess: hasOTPEmailSent, isError: hasErroredSendingOTP, reset: resetGetOTPState },
	] = useGenerateOTPMutation();
	const [
		validateOTP,
		{
			data: validatedOTPData,
			isSuccess: hasValidatedOTP,
			isError: hasErroredOnValidateOTP,
			reset: resetValidateOTPState,
		},
	] = useValidateOTPMutation();
	const [resetPassword, { isSuccess: hasResetPassword, isError: hasErroredOnPasswordReset }] =
		useResetPasswordMutation();

	const handleSecondary = useCallback(() => {
		switch (step) {
			case '1-email':
				// redirect to login
				navigate(ROUTE_LOGIN);
				break;
			case '2-otp':
				resetGetOTPState();
				setIsLoading(true);
				// API call for requesting a new OTP via email
				getOTP({ emailId: email });
				break;
			// default:
			// 	break;
		}
	}, [step, resetGetOTPState, getOTP]);

	const handlePrimary = useCallback(
		(e: SyntheticEvent) => {
			// prevent default form submit behavior
			e.preventDefault();

			const { allValid } = validatePassword(newPassword);

			switch (step) {
				case '1-email':
					if (email === '') {
						setInputOneError(NO_EMAIL_ERROR);
						break;
					}
					if (!emailRegex.test(email)) {
						setInputOneError(INVALID_EMAIL_ERROR);
						break;
					}
					setInputOneError('');
					setIsLoading(true);
					// API call for requesting a OTP via email
					getOTP({ emailId: email });
					break;
				case '2-otp':
					if (code === '' || code.length !== 6) {
						setInputOneError(NO_VERIFICATION_CODE_ERROR);
						break;
					}
					setInputOneError('');
					resetValidateOTPState();
					setIsLoading(true);
					// API call to validate OTP
					validateOTP({ emailId: email, otp: code });
					break;
				case '3-reset-password':
					if (!validatedOTPData) {
						console.log('Missing reset token');
						break;
					}
					if (newPassword === '') {
						setInputOneError(NO_PASSWORD_ERROR);
						break;
					}

					if (!allValid) {
						setInputOneError(PASSWORD_REQUIREMENT_NOT_MET);
						break;
					}
					if (newPassword !== confirmPassword) {
						setInputTwoError(PASSWORD_REQUIREMENT_NOT_MATCHING);
						break;
					}
					setInputOneError('');
					setInputTwoError('');
					setIsLoading(true);
					// API call to change password
					resetPassword({ emailId: email, newPassword, resetToken: validatedOTPData.resetToken });
					break;
				// default:
				// 	break;
			}
		},
		[email, code, newPassword, confirmPassword, step]
	);

	useEffect(() => {
		if (hasOTPEmailSent) {
			setIsLoading(false);
			setStep('2-otp');
		}
		if (hasErroredSendingOTP) {
			setIsLoading(false);
			console.log('error requesting an OTP');
			// TODO: add toast message for failure?
		}
	}, [hasOTPEmailSent, hasErroredSendingOTP]);

	useEffect(() => {
		if (hasValidatedOTP) {
			setIsLoading(false);
			setStep('3-reset-password');
		}
		if (hasErroredOnValidateOTP) {
			setIsLoading(false);
			const nextAttempts = otpAttempts + 1;
			setOtpAttempts(nextAttempts);
			if (nextAttempts >= MAX_OTP_ATTEMPTS) {
				navigate(`${ROUTE_FORGOT_PASSWORD}/${ROUTE_ERROR}`);
				return;
			}
			setInputOneError(NO_VERIFICATION_CODE_ERROR);
		}
	}, [hasValidatedOTP, hasErroredOnValidateOTP]);

	useEffect(() => {
		if (hasResetPassword) {
			setIsLoading(false);
			// redirect to login
			navigate(ROUTE_LOGIN);
		}
		if (hasErroredOnPasswordReset) {
			setIsLoading(false);
			navigate(`${ROUTE_FORGOT_PASSWORD}/${ROUTE_ERROR}`);
		}
	}, [hasResetPassword, hasErroredOnPasswordReset]);

	const handleEmailBlur: FocusEventHandler<HTMLInputElement> = () => {
		let errorMessage = '';
		if (!emailRegex.test(email)) {
			errorMessage = INVALID_EMAIL_ERROR;
		}
		if (email === '') {
			errorMessage = NO_EMAIL_ERROR;
		}
		setInputOneError(errorMessage);
	};

	const handleNewPasswordChange: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
		if (value && !touchedPassword) {
			setTouchedPassword(true);
		}
		setNewPassword(value);

		// Error checking
		let errorMessage = '';
		let confirmErrorMessage = '';

		if (value !== confirmPassword) {
			confirmErrorMessage = PASSWORD_REQUIREMENT_NOT_MATCHING;
		}
		const { allValid } = validatePassword(value);
		if (!allValid) {
			errorMessage = PASSWORD_REQUIREMENT_NOT_MET;
		}
		if (!value) {
			errorMessage = NO_PASSWORD_ERROR;
		}
		setInputOneError(errorMessage);
		setInputTwoError(confirmErrorMessage);
	};

	const handlePasswordBlur: FocusEventHandler<HTMLInputElement> = ({ target: { id, value } }) => {
		setTouchedPassword(true);
		if (!value) {
			if (id === 'new-password') {
				setInputOneError(NO_PASSWORD_ERROR);
			}
			if (id === 'confirm-password') {
				setInputTwoError(NO_PASSWORD_ERROR);
			}
		}
	};

	const handleConfirmPasswordChange: ChangeEventHandler<HTMLInputElement> = ({
		target: { value },
	}) => {
		if (value && !touchedPassword) {
			setTouchedPassword(true);
		}
		setConfirmPassword(value);

		// Error checking
		if (!value) {
			setInputTwoError(NO_PASSWORD_ERROR);
			return;
		}
		if (value !== newPassword) {
			setInputTwoError(PASSWORD_REQUIREMENT_NOT_MATCHING);
			return;
		}
		setInputTwoError('');
	};

	return (
		<Stack>
			{isLoading && <SpinnerModal />}
			<form onSubmit={handlePrimary}>
				<Stack>
					<Typography
						component="h1"
						variant="h1"
						sx={{ font: 'bold 2.25rem "Huntington Serif", serif' }}
					>
						{getTitle(step)}
					</Typography>
					<Typography sx={{ padding: '1.5rem 0 1rem' }}>{getDescription(step)}</Typography>

					{step === '1-email' && (
						<TextField
							error={!!inputOneError}
							fullWidth
							id="email"
							label={EMAIL_ADDRESS}
							onBlur={handleEmailBlur}
							onChange={(event) => setEmail(event.target.value)}
							type="email"
							value={email}
						/>
					)}
					{step === '2-otp' && (
						<TextField
							error={!!inputOneError}
							id="2fa-code"
							label={VERIFICATION_CODE_LABEL}
							placeholder={VERIFY_OTP_INPUT_PLACEHOLDER}
							type="text"
							onBlur={() =>
								code ? setInputOneError('') : setInputOneError(NO_VERIFICATION_CODE_ERROR)
							}
							onChange={(event) => {
								const value = event.target.value;
								if (/^\d*$/.test(value) && value.length <= 6) {
									setCode(value);
								}
							}}
							inputProps={{
								maxLength: 6,
								inputMode: 'numeric',
								pattern: '[0-9]*',
							}}
							value={code}
						/>
					)}
					{step === '3-reset-password' && (
						<PasswordInput
							error={!!inputOneError}
							id="new-password"
							label={NEW_PASSWORD}
							onBlur={handlePasswordBlur}
							onChange={handleNewPasswordChange}
							value={newPassword}
						/>
					)}

					<Collapse in={!!inputOneError} mountOnEnter unmountOnExit>
						<ValidationMessage id="input-error" sx={{ mt: '0.25rem' }}>
							<span>{inputOneError}</span>
						</ValidationMessage>
					</Collapse>

					<Collapse in={step === '3-reset-password' && touchedPassword} mountOnEnter unmountOnExit>
						<PasswordRequirements password={newPassword} />
					</Collapse>

					<Collapse
						in={step === '3-reset-password' && touchedPassword && !inputOneError}
						mountOnEnter
						unmountOnExit
					>
						<SuccessMessage>{PASSWORD_REQUIREMENT_MEETS}</SuccessMessage>
					</Collapse>

					{step === '3-reset-password' && (
						<PasswordInput
							error={!!inputTwoError}
							id="confirm-password"
							label={CONFIRM_PASSWORD}
							onBlur={handlePasswordBlur}
							onChange={handleConfirmPasswordChange}
							sx={{ marginTop: '1rem' }}
							value={confirmPassword}
						/>
					)}

					<Collapse in={!!inputTwoError} mountOnEnter unmountOnExit>
						<ValidationMessage id="input-two-error" sx={{ mt: '0.25rem' }}>
							<span>{inputTwoError}</span>
						</ValidationMessage>
					</Collapse>

					<Collapse
						in={
							step === '3-reset-password' &&
							!inputTwoError &&
							!!confirmPassword &&
							newPassword === confirmPassword
						}
						mountOnEnter
						unmountOnExit
					>
						<SuccessMessage>{PASSWORD_REQUIREMENT_MATCHING}</SuccessMessage>
					</Collapse>
				</Stack>

				<Stack
					alignItems="center"
					direction="row"
					justifyContent={step === '3-reset-password' ? 'flex-end' : 'space-between'}
					spacing={2}
					paddingTop="3.25rem"
				>
					{step !== '3-reset-password' && (
						<Link
							onClick={handleSecondary}
							sx={{ cursor: 'pointer', ':hover': { color: ColorBaseCharcoal } }}
						>
							{step === '1-email' && CANCEL}
							{step === '2-otp' && NEW_OTP_CODE}
						</Link>
					)}
					<Button onClick={handlePrimary} type="submit">
						{step === '1-email' && CONTINUE}
						{step === '2-otp' && CONTINUE}
						{step === '3-reset-password' && SUBMIT}
					</Button>
				</Stack>
			</form>
		</Stack>
	);
}

function getTitle(step: Step) {
	switch (step) {
		case '1-email':
			return FORGOT_PASSWORD_TITLE;
		case '2-otp':
			return CONFIRM_CODE_TITLE;
		case '3-reset-password':
			return RESET_PASSWORD_TITLE;
	}
}

function getDescription(step: Step) {
	switch (step) {
		case '1-email':
			return FORGOT_PASSWORD_DESCRIPTION;
		case '2-otp':
			return CONFIRM_CODE_DESCRIPTION;
		case '3-reset-password':
			return RESET_PASSWORD_DESCRIPTION;
	}
}
